import React, { useState } from 'react';
import { 
  UserCircleIcon, 
  ShieldCheckIcon, 
  MapIcon, 
  ClockIcon, 
  KeyIcon,
  ExclamationTriangleIcon,
  CheckBadgeIcon,
  PencilSquareIcon
} from '@heroicons/react/24/outline';

const EmployeeProfileAdmin = () => {
  // 1. STATE MANAGEMENT (Admin Controls)
  const [governance, setGovernance] = useState({
    status: 'Active',
    accessLevel: 'Normal',
    role: 'Senior Frontend Engineer',
    manager: 'Sarah Connor',
    department: 'Engineering',
    mfaEnabled: true,
  });

  const [auditInfo] = useState({
    lastLogin: 'Jan 07, 2026 - 09:42 AM',
    lastModifiedBy: 'SuperAdmin_Rahul',
    lastPermissionChange: 'Dec 12, 2025'
  });

  const handleUpdate = (field, value) => {
    setGovernance(prev => ({ ...prev, [field]: value }));
    console.log(`System Governance Update: ${field} set to ${value}`);
  };

  const triggerPasswordReset = () => {
    console.warn("Security Event: Force Password Reset triggered for UID: 99281");
  };

  return (
    <div className="min-h-screen bg-slate-50 p-6 font-roboto text-slate-800">
      <div className="max-w-6xl mx-auto flex flex-col md:flex-row gap-6">
        
        {/* 1. PROFILE SIDEBAR (Identity & Presence) */}
        <aside className="w-full md:w-80 space-y-4">
          <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm flex flex-col items-center text-center">
            <div className="relative">
              <div className="w-24 h-24 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 mb-4 border-4 border-white shadow-md">
                <span className="text-3xl font-bold font-poppins">AM</span>
              </div>
              <div className={`absolute bottom-5 right-1 w-5 h-5 border-4 border-white rounded-full ${governance.status === 'Active' ? 'bg-emerald-500' : 'bg-rose-500'}`} />
            </div>
            
            <h2 className="font-poppins text-lg font-bold text-slate-900 leading-tight">Arjun Mehta</h2>
            <p className="text-xs font-medium text-slate-500 mb-3">{governance.role}</p>
            
            <span className={`text-[10px] px-3 py-1 rounded-full font-bold uppercase tracking-wider ${
              governance.status === 'Active' ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'
            }`}>
              {governance.status}
            </span>

            <div className="w-full mt-6 pt-6 border-t border-slate-100 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-[11px] font-bold text-slate-400 uppercase italic">Experience</span>
                <span className="text-xs font-bold text-slate-700">4.2 Years</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-[11px] font-bold text-slate-400 uppercase italic">Employee ID</span>
                <span className="text-xs font-mono font-bold text-slate-700">EMP-99281</span>
              </div>
            </div>
          </div>

          {/* 3. AUDIT INFO PANEL */}
          <div className="bg-slate-900 text-slate-300 rounded-lg p-4 shadow-lg">
            <div className="flex items-center gap-2 mb-4 border-b border-slate-700 pb-2">
              <ClockIcon className="w-4 h-4 text-indigo-400" />
              <h3 className="text-[10px] font-bold uppercase tracking-widest">Audit Governance</h3>
            </div>
            <div className="space-y-3">
              <div>
                <p className="text-[9px] text-slate-500 uppercase font-bold">Last System Login</p>
                <p className="text-[11px] text-white font-medium">{auditInfo.lastLogin}</p>
              </div>
              <div>
                <p className="text-[9px] text-slate-500 uppercase font-bold">Modified By</p>
                <p className="text-[11px] text-indigo-300 font-medium italic">@{auditInfo.lastModifiedBy}</p>
              </div>
              <div>
                <p className="text-[9px] text-slate-500 uppercase font-bold">Permission Change</p>
                <p className="text-[11px] text-white font-medium">{auditInfo.lastPermissionChange}</p>
              </div>
            </div>
          </div>
        </aside>

        {/* 2. ADMIN CONTROL SECTIONS (Main Workspace) */}
        <main className="flex-1 space-y-6">
          
          {/* Header Action Row */}
          <div className="flex justify-between items-center bg-white p-4 border border-slate-200 rounded-lg shadow-sm">
            <div className="flex items-center gap-3">
              <ShieldCheckIcon className="w-6 h-6 text-indigo-600" />
              <div>
                <h1 className="font-poppins text-sm font-bold text-slate-800 uppercase tracking-tight">Governance & Control</h1>
                <p className="text-[10px] text-slate-400">Admin-only panel for employee lifecycle & security</p>
              </div>
            </div>
            <button 
              onClick={() => alert("Changes synchronized with HRMS core.")}
              className="px-4 py-1.5 text-xs font-bold bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors shadow-sm active:scale-95"
            >
              Sync Changes
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            
            {/* Reporting Structure Card */}
            <section className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MapIcon className="w-4 h-4 text-slate-500" />
                  <h3 className="text-[11px] font-bold text-slate-700 uppercase">Reporting Structure</h3>
                </div>
                <PencilSquareIcon className="w-3.5 h-3.5 text-slate-400 cursor-pointer hover:text-indigo-600" />
              </div>
              <div className="p-4 space-y-4">
                <div>
                  <label className="text-[10px] font-bold text-slate-400 uppercase block mb-1">Direct Manager</label>
                  <select 
                    value={governance.manager}
                    onChange={(e) => handleUpdate('manager', e.target.value)}
                    className="w-full text-xs font-bold border-slate-200 rounded p-2 bg-white focus:ring-1 focus:ring-indigo-500 outline-none"
                  >
                    <option>Sarah Connor</option>
                    <option>David Hall</option>
                    <option>Megha Singh</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-bold text-slate-400 uppercase block mb-1">Department</label>
                  <div className="p-2 bg-slate-50 rounded text-xs font-bold text-slate-600 border border-slate-100">
                    {governance.department}
                  </div>
                </div>
              </div>
            </section>

            {/* Account Status Card */}
            <section className="bg-white border border-slate-200 rounded-lg shadow-sm">
              <div className="px-4 py-3 border-b border-slate-100 bg-slate-50 flex items-center gap-2">
                <ExclamationTriangleIcon className="w-4 h-4 text-rose-500" />
                <h3 className="text-[11px] font-bold text-slate-700 uppercase">Account Lifecycle</h3>
              </div>
              <div className="p-4 space-y-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-bold text-slate-700">Account Status</p>
                    <p className="text-[10px] text-slate-400 italic">Suspend access immediately</p>
                  </div>
                  <button 
                    onClick={() => handleUpdate('status', governance.status === 'Active' ? 'Suspended' : 'Active')}
                    className={`px-3 py-1 text-[10px] font-bold rounded uppercase tracking-tighter transition-all ${
                      governance.status === 'Active' ? 'bg-rose-50 text-rose-600 hover:bg-rose-100' : 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100'
                    }`}
                  >
                    {governance.status === 'Active' ? 'Suspend Account' : 'Re-Activate'}
                  </button>
                </div>
                <div className="pt-4 border-t border-slate-50">
                  <button 
                    onClick={triggerPasswordReset}
                    className="w-full py-2 bg-slate-100 hover:bg-slate-200 text-slate-600 text-[10px] font-bold rounded uppercase tracking-wider transition-colors flex items-center justify-center gap-2"
                  >
                    <KeyIcon className="w-3.5 h-3.5" /> Force Password Reset
                  </button>
                </div>
              </div>
            </section>

            {/* Access Level Card */}
            <section className="bg-white border border-slate-200 rounded-lg shadow-sm lg:col-span-2">
              <div className="px-4 py-3 border-b border-slate-100 bg-slate-50 flex items-center gap-2">
                <CheckBadgeIcon className="w-4 h-4 text-emerald-600" />
                <h3 className="text-[11px] font-bold text-slate-700 uppercase">Role & Access Governance</h3>
              </div>
              <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="text-[10px] font-bold text-slate-400 uppercase block mb-1">System Role</label>
                  <select 
                    value={governance.role}
                    onChange={(e) => handleUpdate('role', e.target.value)}
                    className="w-full text-xs font-bold border-slate-200 rounded p-2 bg-white outline-none focus:ring-1 focus:ring-indigo-500"
                  >
                    <option>Senior Frontend Engineer</option>
                    <option>Project Manager</option>
                    <option>HR Business Partner</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-bold text-slate-400 uppercase block mb-1">Access Tier</label>
                  <div className="flex gap-2">
                    {['Normal', 'Elevated'].map(level => (
                      <button 
                        key={level}
                        onClick={() => handleUpdate('accessLevel', level)}
                        className={`flex-1 py-1.5 text-[10px] font-bold rounded border transition-all ${
                          governance.accessLevel === level 
                            ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm' 
                            : 'bg-white text-slate-400 border-slate-200 hover:border-indigo-300'
                        }`}
                      >
                        {level}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-3 bg-slate-50 p-3 rounded-lg border border-slate-100">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${governance.mfaEnabled ? 'bg-emerald-100' : 'bg-slate-200'}`}>
                    <ShieldCheckIcon className={`w-5 h-5 ${governance.mfaEnabled ? 'text-emerald-600' : 'text-slate-400'}`} />
                  </div>
                  <div>
                    <p className="text-[11px] font-bold text-slate-700">MFA Status</p>
                    <p className={`text-[10px] font-bold ${governance.mfaEnabled ? 'text-emerald-600' : 'text-slate-400'}`}>
                      {governance.mfaEnabled ? 'SECURED / ACTIVE' : 'NOT CONFIGURED'}
                    </p>
                  </div>
                </div>
              </div>
            </section>

          </div>
        </main>
      </div>
    </div>
  );
};

export default EmployeeProfileAdmin;